// Login.jsx - Complete Final Version
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const getBackendUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'production') return 'https://api.leave-management.manurevasolutions.com';
  return 'http://localhost:5000';
};

const BACKEND_URL = getBackendUrl();

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908C16.658 14.251 17.64 11.943 17.64 9.2z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

const Login = ({ error, onClearError }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [displayError, setDisplayError] = useState(error || null);
  const [emailCheckResult, setEmailCheckResult] = useState(null);
  const [showGoogleLogin, setShowGoogleLogin] = useState(false);

  // ✅ Responsive
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuthStatus, isAuthenticated, userRole, isLoading: authLoading } = useAuth();

  // ✅ Check URL error params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    if (errorParam === 'account_deactivated') {
      setDisplayError('Your account has been deactivated. Please contact HR to reactivate your account.');
      setEmailCheckResult({ deleted: true });
    } else if (errorParam === 'account_inactive') {
      setDisplayError('Your account is currently inactive. Please contact HR for assistance.');
      setEmailCheckResult({ deleted: true });
    }
  }, [location]);

  // ✅ Auto-redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && userRole && !authLoading) {
      const redirectPath = {
        'Superadmin': '/Admindashboard',
        'CEO': '/Attendance',
        'HR': '/Hrdashboard',
        'Manager': '/managerdashboard',
        'Employee': '/Worker',
        'Consultant': '/Timesheet'
      }[userRole] || '/';
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, userRole, authLoading, navigate]);

  const handleAutoLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setDisplayError('Email is required'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setDisplayError('Please enter a valid email address'); return; }

    setIsLoading(true);
    setDisplayError(null);
    setEmailCheckResult(null);

    try {
      const checkResponse = await fetch(`${BACKEND_URL}/auth/check-email`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const checkData = await checkResponse.json();

      if (checkData.deleted || checkData.is_deleted) {
        setEmailCheckResult({ exists: false, email, deleted: true });
        setDisplayError(`The account for "${email}" has been deactivated. Please contact HR if you believe this is an error.`);
        setIsLoading(false); return;
      }
      if (!checkData.exists) {
        setEmailCheckResult({ exists: false, email });
        setDisplayError(`Email "${email}" not found in the system. Please contact HR to register.`);
        setIsLoading(false); return;
      }
      if (checkData.status === 'inactive' || checkData.status === 'deleted') {
        setEmailCheckResult({ exists: false, email, deleted: true });
        setDisplayError(`Your account is currently ${checkData.status}. Please contact HR for assistance.`);
        setIsLoading(false); return;
      }

      setEmailCheckResult(checkData);

      const loginResponse = await fetch(`${BACKEND_URL}/auth/auto-login`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const loginData = await loginResponse.json();

      if (!loginResponse.ok) {
        if (loginResponse.status === 403) {
          setEmailCheckResult({ exists: false, email, deleted: true });
          throw new Error(loginData.error || 'Your account has been deactivated. Please contact HR.');
        }
        throw new Error(loginData.error || 'Auto-login failed');
      }

      setEmailCheckResult({ ...checkData, loginSuccess: true });
      await checkAuthStatus();

      setTimeout(() => {
        // ✅ Return to previous page if session expired
        const returnTo = sessionStorage.getItem('returnTo');
        if (returnTo && returnTo !== '/' && returnTo !== '/login') {
          sessionStorage.removeItem('returnTo');
          navigate(returnTo, { replace: true });
        } else {
          navigate(loginData.redirect_path, { replace: true });
        }
      }, 500);

    } catch (err) {
      setDisplayError(err.message || 'Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  const clearError = useCallback(() => {
    setDisplayError(null);
    setEmailCheckResult(null);
    if (onClearError) onClearError();
  }, [onClearError]);

  const handleGoogleLogin = useCallback(() => {
    setIsLoading(true);
    window.location.href = `${BACKEND_URL}/auth/google`;
  }, []);

  const busy = isLoading || authLoading;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isMobile
        ? '#f0f2f5'
        : 'linear-gradient(135deg, #e8f0fe 0%, #fef9e7 50%, #e8f5e9 100%)',
      fontFamily: "'Inter', Arial, sans-serif",
      padding: isMobile ? '16px' : '20px',
      boxSizing: 'border-box',
    }}>

      {/* ── Main Card ── */}
      <div style={{
        display: 'flex',
        width: '100%',
        maxWidth: isMobile ? '420px' : isTablet ? '700px' : '920px',
        minHeight: isMobile ? 'auto' : '560px',
        borderRadius: isMobile ? '20px' : '24px',
        overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.14)',
        background: '#fff',
      }}>

        {/* ── LEFT: Form Panel ── */}
        <div style={{
          flex: isMobile ? '1' : '0 0 420px',
          padding: isMobile ? '32px 24px' : isTablet ? '40px 36px' : '48px 44px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: '#fff',
          width: isMobile ? '100%' : 'auto',
        }}>

          {/* Logo */}
          <img
            src="/Images/Logo1.jpg"
            alt="Manureva"
            style={{
              width: isMobile ? '110px' : '130px',
              height: 'auto',
              marginBottom: isMobile ? '20px' : '28px',
              borderRadius: '8px',
            }}
          />

          {!showGoogleLogin ? (
            <>
              <h1 style={{
                fontSize: isMobile ? '22px' : '24px',
                fontWeight: '700', color: '#111827',
                margin: '0 0 6px',
              }}>
                Sign in
              </h1>
              <p style={{
                fontSize: '13px', color: '#6b7280',
                margin: '0 0 24px', lineHeight: 1.6,
              }}>
                Use your company email to continue.
              </p>

              <form onSubmit={handleAutoLogin} style={{ width: '100%' }}>
                <label style={{
                  display: 'block', fontSize: '11px', fontWeight: '600',
                  color: '#9ca3af', letterSpacing: '0.6px',
                  textTransform: 'uppercase', marginBottom: '6px',
                }}>
                  Work Email
                </label>

                <div style={{ position: 'relative', marginBottom: '16px' }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@manurevasolutions.net"
                    required
                    disabled={busy}
                    style={{
                      width: '100%',
                      padding: '13px 16px',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '14px',
                      color: '#111827',
                      outline: 'none',
                      background: '#f9fafb',
                      boxSizing: 'border-box',
                      transition: 'all 0.15s',
                      fontFamily: "'Inter', sans-serif",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1a2a5e';
                      e.target.style.background = '#fff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(26,42,94,0.08)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.background = '#f9fafb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Continue button */}
                <button
                  type="submit"
                  disabled={busy}
                  style={{
                    width: '100%', padding: '14px',
                    background: busy ? '#9ca3af' : '#1a2a5e',
                    color: '#fff', border: 'none', borderRadius: '12px',
                    fontSize: '15px', fontWeight: '600',
                    cursor: busy ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '8px',
                    marginBottom: '14px', transition: 'all 0.15s',
                    fontFamily: "'Inter', sans-serif",
                  }}
                  onMouseOver={(e) => { if (!busy) e.currentTarget.style.background = '#243580'; }}
                  onMouseOut={(e) => { if (!busy) e.currentTarget.style.background = busy ? '#9ca3af' : '#1a2a5e'; }}
                >
                  {busy ? (
                    <>
                      <div style={{
                        width: '16px', height: '16px',
                        border: '2.5px solid rgba(255,255,255,0.3)',
                        borderTop: '2.5px solid #fff',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }} />
                      <span>Checking…</span>
                    </>
                  ) : 'Continue →'}
                </button>

                {/* Divider */}
                {/* <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>or</span>
                  <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                </div> */}

                {/* Google button */}
                {/* <button
                  type="button"
                  onClick={() => setShowGoogleLogin(true)}
                  style={{
                    width: '100%', padding: '13px',
                    background: '#fff', color: '#374151',
                    border: '1.5px solid #e5e7eb', borderRadius: '12px',
                    fontSize: '14px', fontWeight: '500', cursor: 'pointer',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '10px',
                    transition: 'all 0.15s', fontFamily: "'Inter', sans-serif",
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#d1d5db'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >
                  <GoogleLogo />
                  Continue with Google
                </button> */}
              </form>

              <p style={{
                fontSize: '11px', color: '#9ca3af',
                textAlign: 'center', marginTop: '20px', lineHeight: 1.5,
              }}>
                By signing in, you agree to use this system for leave management purposes only.
              </p>
            </>
          ) : (
            <>
              <h1 style={{ fontSize: isMobile ? '22px' : '24px', fontWeight: '700', color: '#111827', margin: '0 0 6px' }}>
                Welcome back
              </h1>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 28px', lineHeight: 1.6 }}>
                Sign in with your company Google account.
              </p>

              <button
                onClick={handleGoogleLogin}
                disabled={busy}
                style={{
                  width: '100%', padding: '14px',
                  background: '#fff', color: '#374151',
                  border: '1.5px solid #e5e7eb', borderRadius: '12px',
                  fontSize: '15px', fontWeight: '500', cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '10px',
                  marginBottom: '12px', fontFamily: "'Inter', sans-serif",
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; }}
              >
                <GoogleLogo />
                Continue with Google
              </button>

              <button
                onClick={() => setShowGoogleLogin(false)}
                style={{
                  width: '100%', padding: '12px',
                  background: 'transparent', border: '1.5px solid #e5e7eb',
                  borderRadius: '12px', fontSize: '14px', color: '#6b7280',
                  cursor: 'pointer', fontFamily: "'Inter', sans-serif",
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#9ca3af'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
              >
                ← Back to email login
              </button>

              <p style={{
                fontSize: '11px', color: '#9ca3af',
                textAlign: 'center', marginTop: '20px', lineHeight: 1.5,
              }}>
                By signing in, you agree to use this system for leave management purposes only.
              </p>
            </>
          )}
        </div>

        {/* ── RIGHT: Image Panel — hidden on mobile ── */}
        {!isMobile && (
          <div style={{
            flex: 1, position: 'relative',
            overflow: 'hidden', background: '#1a2a5e',
          }}>
            <img
              src="/Images/Manurevalogo.png"
              alt="Manureva"
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover', opacity: 0.18,
              }}
            />
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(135deg, #1a2a5e 0%, #0f1a3e 60%, #1a3a2a 100%)',
              opacity: 0.92,
            }} />
            <div style={{
              position: 'relative', zIndex: 1,
              padding: isTablet ? '36px 32px' : '48px 44px',
              height: '100%', display: 'flex',
              flexDirection: 'column', justifyContent: 'space-between',
              boxSizing: 'border-box',
            }}>
              {/* Top badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '20px', padding: '6px 14px',
                alignSelf: 'flex-start',
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: '#4ade80', animation: 'pulse 2s infinite',
                }} />
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>
                  System Online
                </span>
              </div>

              {/* Center content */}
              <div>
                <div style={{
                  width: '48px', height: '4px',
                  background: '#4f8ef7', borderRadius: '2px', marginBottom: '20px',
                }} />
                <h2 style={{
                  fontSize: isTablet ? '22px' : '28px',
                  fontWeight: '700', color: '#fff',
                  margin: '0 0 14px', lineHeight: 1.3, letterSpacing: '-0.5px',
                }}>
                  Manage your leave,<br />
                  <span style={{ color: '#4f8ef7' }}>effortlessly.</span>
                </h2>
                <p style={{
                  fontSize: '14px', color: 'rgba(255,255,255,0.6)',
                  lineHeight: 1.7, margin: '0 0 28px', maxWidth: '280px',
                }}>
                  Request, track, and approve time off from one unified dashboard — built for every role.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[
                    { icon: '📅', text: 'Smart leave tracking' },
                    { icon: '✅', text: 'One-click approvals' },
                    { icon: '📊', text: 'Real-time balance updates' },
                  ].map((f, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                    }}>
                      <span style={{ fontSize: '16px' }}>{f.icon}</span>
                      <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>
                        {f.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div style={{
                fontSize: '12px', color: 'rgba(255,255,255,0.3)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>© {new Date().getFullYear()} Manureva Digital Solutions</span>
                <span style={{
                  background: 'rgba(255,255,255,0.08)',
                  padding: '4px 10px', borderRadius: '20px',
                  color: 'rgba(255,255,255,0.5)',
                }}>v2.0</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Error Modal ── */}
      {displayError && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000, backdropFilter: 'blur(4px)', padding: '16px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '20px',
            padding: isMobile ? '24px' : '32px',
            maxWidth: '400px', width: '100%',
            boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%', flexShrink: 0,
                background: emailCheckResult?.deleted ? '#fffbeb' : '#fef2f2',
                border: `1.5px solid ${emailCheckResult?.deleted ? '#fde68a' : '#fecaca'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px',
              }}>
                {emailCheckResult?.deleted ? '🚫' : '⚠️'}
              </div>
              <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#111827', margin: 0 }}>
                {emailCheckResult?.deleted ? 'Account Deactivated' : 'Login Error'}
              </h3>
            </div>

            <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.6, marginBottom: '16px' }}>
              {displayError}
            </p>

            {emailCheckResult?.deleted && (
              <div style={{
                background: '#fef3c7', border: '1.5px solid #fbbf24',
                borderRadius: '10px', padding: '12px 14px', marginBottom: '16px',
              }}>
                <p style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', margin: '0 0 4px' }}>
                  📧 Need to reactivate?
                </p>
                <p style={{ fontSize: '13px', color: '#92400e', lineHeight: 1.5, margin: 0 }}>
                  Contact HR at: <strong>hr@manurevasolutions.com</strong>
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={clearError} style={{
                flex: 1, padding: '12px',
                background: '#1a2a5e', color: '#fff',
                border: 'none', borderRadius: '10px',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                fontFamily: "'Inter', sans-serif",
              }}>
                {emailCheckResult?.deleted ? 'Understood' : 'OK'}
              </button>
              {!emailCheckResult?.exists && !emailCheckResult?.deleted && (
                <button onClick={() => { clearError(); setShowGoogleLogin(true); }} style={{
                  flex: 1, padding: '12px',
                  background: '#10b981', color: '#fff',
                  border: 'none', borderRadius: '10px',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                }}>
                  Use Google login
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Success Modal ── */}
      {emailCheckResult?.loginSuccess && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 2000, backdropFilter: 'blur(6px)', padding: '16px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '24px',
            padding: isMobile ? '32px 24px' : '40px 44px',
            textAlign: 'center',
            boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
            maxWidth: '360px', width: '100%',
            animation: 'slideUp 0.3s ease-out',
          }}>
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 8px 24px rgba(16,185,129,0.35)',
              animation: 'popIn 0.4s ease-out',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>

            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 8px' }}>
              Login Successful!
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 24px', lineHeight: 1.6 }}>
              Welcome back, <strong style={{ color: '#1a2a5e' }}>
                {emailCheckResult?.name || 'User'}
              </strong>!<br />
              Redirecting to your dashboard…
            </p>

            <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(90deg, #10b981, #059669)',
                borderRadius: '2px',
                animation: 'progressBar 0.5s ease-out forwards',
              }} />
            </div>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '12px 0 0' }}>Please wait…</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes popIn {
          0%   { transform: scale(0.5); opacity: 0; }
          70%  { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes progressBar {
          from { width: 0%; }
          to   { width: 100%; }
        }
        * { box-sizing: border-box; }
      `}</style>
    </div>
  );
};

export default Login;